import { useConnection } from "@shared/hooks/useConnection";
import { ConnectionState } from "@shared/types/connection.types";
import theme from "@shared/utils/themeColors";
import { Check, UserPlus, X } from "lucide-react";
import React, { useState } from "react";

interface ConnectionButtonProps {
  userId: string;
  variant?: "default" | "profile";
  className?: string;
  initialState?: ConnectionState;
}

export const ConnectionButton: React.FC<ConnectionButtonProps> = ({
  userId,
  variant = "default",
  initialState,
  className,
}) => {
  const {
    isConnected,
    isPending,
    isReceiver,
    loading,
    error,
    sendRequest,
    approveRequest,
    rejectRequest,
  } = useConnection(userId, initialState);

  const getButtonClasses = (baseClasses?: string) => {
    const classes = ["cursor-pointer"];

    if (variant === "profile") {
      classes.push(
        "btn-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
      );
    } else {
      classes.push(
        `block text-center text-sm ${theme.text.link} font-medium px-3 py-4 w-full`
      );
    }

    if (baseClasses) {
      classes.push(baseClasses);
    }

    if (className) {
      classes.push(className);
    }

    return classes.join(" ");
  };

  if (loading) {
    return (
      <button disabled className={getButtonClasses()}>
        <div className="flex items-center justify-center gap-2">
          <svg
            className="animate-spin fill-current shrink-0"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
          </svg>
          <div>Loading...</div>
        </div>
      </button>
    );
  }

  if (error) {
    return (
      <button disabled className={getButtonClasses()}>
        Error
      </button>
    );
  }

  if (isConnected) {
    return (
      <button disabled className={getButtonClasses()}>
        <div className="flex items-center justify-center">
          <svg
            className="shrink-0 fill-current mr-1.5"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M11 0c1.3 0 2.6.5 3.5 1.5 1 .9 1.5 2.2 1.5 3.5 0 1.3-.5 2.6-1.4 3.5l-1.2 1.2c-.2.2-.5.3-.7.3-.2 0-.5-.1-.7-.3-.4-.4-.4-1 0-1.4l1.1-1.2c.6-.5.9-1.3.9-2.1s-.3-1.6-.9-2.2C12 1.7 10 1.7 8.9 2.8L7.7 4c-.4.4-1 .4-1.4 0-.4-.4-.4-1 0-1.4l1.2-1.1C8.4.5 9.7 0 11 0zM8.3 12c.4-.4 1-.5 1.4-.1.4.4.4 1 0 1.4l-1.2 1.2C7.6 15.5 6.3 16 5 16c-1.3 0-2.6-.5-3.5-1.5C.5 13.6 0 12.3 0 11c0-1.3.5-2.6 1.5-3.5l1.1-1.2c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4L2.9 8.9c-.6.5-.9 1.3-.9 2.1s.3 1.6.9 2.2c1.1 1.1 3.1 1.1 4.2 0L8.3 12zm1.1-6.8c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-4.2 4.2c-.2.2-.5.3-.7.3-.2 0-.5-.1-.7-.3-.4-.4-.4-1 0-1.4l4.2-4.2z" />
          </svg>
          <span>Connected</span>
        </div>
      </button>
    );
  }

  if (isPending) {
    if (isReceiver) {
      return <ReceiverActions onApprove={approveRequest} onReject={rejectRequest} />;
    }

    return (
      <button disabled className={getButtonClasses()}>
        <div className="flex items-center justify-center">
          <svg
            className={`shrink-0 fill-current mr-2`}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M7.586 9H1a1 1 0 1 1 0-2h6.586L6.293 5.707a1 1 0 0 1 1.414-1.414l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 1 1-1.414-1.414L7.586 9ZM3.075 4.572a1 1 0 1 1-1.64-1.144 8 8 0 1 1 0 9.144 1 1 0 0 1 1.64-1.144 6 6 0 1 0 0-6.856Z" />
          </svg>
          <span>Connection request sent</span>
        </div>
      </button>
    );
  }

  return (
    <button onClick={sendRequest} className={getButtonClasses()}>
      <div className="flex items-center justify-center">
        {variant === "profile" && (
          <svg
            className={`shrink-0 fill-current text-gray-400 dark: ${theme.text.muted}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path d="M8 3.414V6a1 1 0 1 1-2 0V1a1 1 0 0 1 1-1h5a1 1 0 0 1 0 2H9.414l6.293 6.293a1 1 0 1 1-1.414 1.414L8 3.414Zm0 9.172V10a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1H4a1 1 0 0 1 0-2h2.586L.293 7.707a1 1 0 0 1 1.414-1.414L8 12.586Z" />
          </svg>
        )}
        <span>Connect</span>
      </div>
    </button>
  );
};

type ReceiverActionsProps = {
  onApprove: () => Promise<unknown> | void;
  onReject: () => Promise<unknown> | void;
};

const ReceiverActions: React.FC<ReceiverActionsProps> = ({
  onApprove,
  onReject,
}) => {
  const [status, setStatus] = useState<
    "idle" | "approving" | "approved" | "rejecting"
  >("idle");

  // TEST MODE: no server call, only animation. Flip to false to restore.
  const TEST_MODE = false;

  const handleApprove = async () => {
    setStatus("approving");
    try {
      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 600));
      } else {
        await onApprove();
      }
      setStatus("approved");
    } catch {
      setStatus("idle");
    }
  };

  const handleReject = async () => {
    setStatus("rejecting");
    try {
      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 600));
        setStatus("idle");
        return;
      }
      await onReject();
    } catch {
      setStatus("idle");
    }
  };

  if (status === "approved") {
    return (
      <div
        className={`flex items-center justify-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2.5 animate-pop-in`}
      >
        <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm">
          <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <Check className="relative h-3.5 w-3.5" strokeWidth={3} />
        </span>
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          Connected!
        </span>
      </div>
    );
  }

  const busy = status === "approving" || status === "rejecting";

  return (
    <div
      className={`relative flex flex-col gap-2 rounded-lg border ${theme.border.primary} ${theme.bg.secondary} p-2.5 shadow-sm animate-receive-request`}
    >
      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
        <span className="absolute inset-0 rounded-full bg-blue-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
      </span>

      <div className="flex items-center gap-1.5 px-0.5">
        <UserPlus
          className={`h-3.5 w-3.5 ${theme.text.brand} animate-wiggle origin-center`}
          strokeWidth={2.5}
        />
        <span className={`text-xs font-medium ${theme.text.secondary}`}>
          Wants to connect
        </span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleApprove}
          disabled={busy}
          aria-label="Accept connection request"
          className={`group flex-1 inline-flex items-center justify-center gap-1.5 rounded-md ${theme.button.approve.base} ${theme.button.approve.dark} ${theme.button.approve.disabled} px-2.5 py-1.5 text-xs font-medium transition-all duration-150 hover:shadow-sm active:scale-[0.97]`}
        >
          {status === "approving" ? (
            <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
          ) : (
            <Check className="h-3.5 w-3.5 transition-transform duration-150 group-hover:scale-110" strokeWidth={2.5} />
          )}
          <span>Accept</span>
        </button>
        <button
          onClick={handleReject}
          disabled={busy}
          aria-label="Reject connection request"
          className={`group flex-1 inline-flex items-center justify-center gap-1.5 rounded-md ${theme.button.reject.base} ${theme.button.reject.dark} ${theme.button.reject.disabled} px-2.5 py-1.5 text-xs font-medium transition-all duration-150 hover:shadow-sm active:scale-[0.97]`}
        >
          {status === "rejecting" ? (
            <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
          ) : (
            <X className="h-3.5 w-3.5 transition-transform duration-150 group-hover:rotate-90" strokeWidth={2.5} />
          )}
          <span>Decline</span>
        </button>
      </div>
    </div>
  );
};
